import { useState, useEffect, useCallback } from 'react';
import { ArrowClockwise, Shield, LockSimple } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const ACTION_COLORS = {
  create: 'hsl(142,70%,45%)',
  update: 'hsl(197,100%,48%)',
  delete: 'hsl(0,84%,60%)',
  status_change: 'hsl(43,96%,56%)',
  payment: 'hsl(282,60%,55%)',
};

function ActionBadge({ action }) {
  const color = ACTION_COLORS[action] || 'hsl(0,0%,50%)';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px',
      border: `1px solid ${color}`, color,
      ...MONO, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em',
    }}>
      {action?.replace(/_/g, ' ')}
    </span>
  );
}

const COLLECTIONS = ['', 'sales_orders', 'invoices', 'users', 'work_orders', 'purchase_orders',
  'dispatch_notes', 'qc_records', 'stock_items', 'customers', 'products'];
const ACTIONS = ['', 'create', 'update', 'delete', 'status_change'];

export default function AuditLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const [filters, setFilters] = useState({
    collection: '', action: '', date_from: '', date_to: '',
  });
  const setF = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(0); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filters.collection) params.set('collection', filters.collection);
      if (filters.action) params.set('action', filters.action);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);

      const [logsRes, sumRes] = await Promise.all([
        api.get(`/audit-logs?${params}`),
        api.get('/audit-logs/summary'),
      ]);
      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setSummary(sumRes.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to load audit logs';
      setError(msg);
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const MONO_SM = { ...MONO, fontSize: 10, color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' };

  if (user && user.role !== 'admin') {
    return (
      <div data-testid="audit-log-page" style={{ display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
        <LockSimple size={48} color="hsl(0,0%,30%)" weight="thin" />
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, color: 'hsl(0,0%,50%)',
          textTransform: 'uppercase', letterSpacing: '0.1em' }}>Admin Access Required</div>
        <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,35%)' }}>
          Your account role <strong style={{ color: 'hsl(0,0%,55%)' }}>{user.role}</strong> does not have permission to view audit logs.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={20} color="hsl(197,100%,48%)" weight="fill" /> Audit Log
          </h1>
          <div style={MONO_SM}>Read-only · Admin access only · {total.toLocaleString()} total entries</div>
        </div>
        <button onClick={() => load()} className="btn-ghost"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowClockwise size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px',
          background: 'hsl(0,60%,8%)', border: '1px solid hsl(0,60%,30%)',
          color: 'hsl(0,80%,65%)', ...MONO, fontSize: 11,
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <LockSimple size={14} weight="fill" />
          {error}
          <button onClick={() => load()} className="btn-ghost"
            style={{ marginLeft: 'auto', fontSize: 10, padding: '3px 10px' }}>
            Retry
          </button>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="metrics-row" style={{ marginBottom: 16, gap: 1 }}>
          <div className="metric-card" style={{ flex: 1 }}>
            <div style={MONO_SM}>Total Entries</div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 28, fontWeight: 700,
              color: 'hsl(197,100%,48%)', marginTop: 6 }}>{(summary.total_entries || 0).toLocaleString()}</div>
          </div>
          {(summary.top_users || []).slice(0, 3).map(u => (
            <div key={u.user} className="metric-card" style={{ flex: 1 }}>
              <div style={MONO_SM}>Top User</div>
              <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700,
                color: 'hsl(140,60%,45%)', marginTop: 6, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.user || '—'}</div>
              <div style={{ ...MONO_SM, marginTop: 4, color: 'hsl(0,0%,40%)' }}>{u.count} actions</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={MONO_SM}>Collection</div>
          <select value={filters.collection} onChange={e => setF('collection', e.target.value)}
            style={{ background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
              padding: '7px 10px', color: 'hsl(0,0%,85%)', ...MONO, fontSize: 11,
              outline: 'none', appearance: 'auto', marginTop: 4 }}>
            <option value="">All Collections</option>
            {COLLECTIONS.filter(Boolean).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div style={MONO_SM}>Action</div>
          <select value={filters.action} onChange={e => setF('action', e.target.value)}
            style={{ background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
              padding: '7px 10px', color: 'hsl(0,0%,85%)', ...MONO, fontSize: 11,
              outline: 'none', appearance: 'auto', marginTop: 4 }}>
            <option value="">All Actions</option>
            {ACTIONS.filter(Boolean).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <div style={MONO_SM}>From Date</div>
          <input type="date" value={filters.date_from} onChange={e => setF('date_from', e.target.value)}
            style={{ background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
              padding: '7px 10px', color: 'hsl(0,0%,85%)', ...MONO, fontSize: 11,
              outline: 'none', marginTop: 4 }} />
        </div>
        <div>
          <div style={MONO_SM}>To Date</div>
          <input type="date" value={filters.date_to} onChange={e => setF('date_to', e.target.value)}
            style={{ background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
              padding: '7px 10px', color: 'hsl(0,0%,85%)', ...MONO, fontSize: 11,
              outline: 'none', marginTop: 4 }} />
        </div>
        {(filters.collection || filters.action || filters.date_from || filters.date_to) && (
          <button onClick={() => { setFilters({ collection: '', action: '', date_from: '', date_to: '' }); setPage(0); }}
            className="btn-ghost" style={{ padding: '7px 12px', fontSize: 10, alignSelf: 'flex-end' }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)' }}>
        <div className="table-scroll">
          <table className="erp-table" data-testid="audit-log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Role</th>
                <th>Action</th>
                <th>Collection</th>
                <th>Document ID</th>
                <th>Description</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center',
                  color: 'hsl(197,100%,48%)', ...MONO, fontSize: 11 }}>Loading...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center',
                  color: 'hsl(0,0%,35%)', ...MONO, fontSize: 11 }}>No audit logs found</td></tr>
              )}
              {logs.map((log, i) => (
                <tr key={i} data-testid={`audit-row-${i}`}>
                  <td style={{ color: 'hsl(0,0%,55%)', whiteSpace: 'nowrap', fontSize: 10 }}>
                    {log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                    }) : '—'}
                  </td>
                  <td style={{ color: 'hsl(0,0%,85%)', fontWeight: 600 }}>{log.user_name || '—'}</td>
                  <td style={{ color: 'hsl(0,0%,50%)', fontSize: 9, textTransform: 'uppercase' }}>
                    {log.user_role || '—'}
                  </td>
                  <td><ActionBadge action={log.action} /></td>
                  <td style={{ color: 'hsl(197,100%,48%)', fontSize: 10 }}>{log.collection || '—'}</td>
                  <td style={{ color: 'hsl(0,0%,45%)', fontSize: 9, maxWidth: 100,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.doc_id || '—'}
                  </td>
                  <td style={{ color: 'hsl(0,0%,70%)', maxWidth: 220,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.description || '—'}
                  </td>
                  <td style={{ maxWidth: 160 }}>
                    {log.changes && Object.keys(log.changes).length > 0 ? (
                      <details style={{ cursor: 'pointer' }}>
                        <summary style={{ ...MONO, fontSize: 9, color: 'hsl(43,96%,56%)' }}>
                          {Object.keys(log.changes).length} field(s)
                        </summary>
                        <pre style={{ ...MONO, fontSize: 8, color: 'hsl(0,0%,55%)',
                          margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {JSON.stringify(log.changes, null, 2)}
                        </pre>
                      </details>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center',
          padding: '16px 0', ...MONO, fontSize: 11, color: 'hsl(0,0%,50%)' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="btn-ghost" style={{ padding: '5px 14px', opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
          <span>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)} · {total.toLocaleString()} entries</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total}
            className="btn-ghost" style={{ padding: '5px 14px', opacity: (page + 1) * PAGE_SIZE >= total ? 0.4 : 1 }}>Next →</button>
        </div>
      )}
    </div>
  );
}
